import { Close, ForumOutlined } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { useCallback } from "react";
import { Panel, PanelResizeHandle } from "react-resizable-panels";
import { useSnackbar } from "src/components/Snackbar";
import { TiptapCollabPanel } from "./collaboration/TiptapCollabPanel";
import { getTiptapCollaborationActivityCount } from "./collaboration/tiptapCommentUtils";
import type {
  TiptapCommentsController,
  TiptapSuggestionsController,
} from "./collaboration/types";

type TiptapCollaborationPanelProps = {
  commentsController?: TiptapCommentsController | null;
  getThreadLinkUrl?: (threadId: string) => string;
  open: boolean;
  onClose: () => void;
  suggestionsController?: TiptapSuggestionsController | null;
};

export const TiptapCollaborationPanel = ({
  commentsController,
  getThreadLinkUrl,
  open,
  onClose,
  suggestionsController,
}: TiptapCollaborationPanelProps) => {
  const { showSnackbar } = useSnackbar();
  const handleThreadAnchorUnavailable = useCallback(() => {
    showSnackbar("The anchor for the comment in the document is not available");
  }, [showSnackbar]);

  if (!open) {
    return null;
  }

  const activityCount = getTiptapCollaborationActivityCount({
    suggestions: suggestionsController?.suggestions ?? [],
    threads: commentsController?.threads ?? [],
  });
  const hasController = Boolean(commentsController || suggestionsController);

  return (
    <>
      <PanelResizeHandle className="w-px bg-gray-200 resize-handle shrink-0" />
      <Panel
        id="tiptap-doc-viewer-collaboration"
        order={1}
        defaultSize={25}
        minSize={15}
        maxSize={50}
        className="min-w-[200px] flex h-[auto] bg-transparent"
      >
        <Box
          className="flex h-full w-full min-w-0 flex-col bg-transparent"
          sx={{ minHeight: 0, overflow: "hidden" }}
        >
          <Box className="flex items-center justify-between py-2 px-3 border-0 border-b border-solid border-gray-200 min-h-[49px]">
            <Box className="flex items-center gap-2">
              <Typography>Collaboration</Typography>
              {hasController ? (
                <Typography variant="caption" className="text-gray-500">
                  ({activityCount})
                </Typography>
              ) : null}
            </Box>
            <IconButton
              aria-label="Close collaboration"
              size="small"
              onClick={onClose}
              color="inherit"
              edge="end"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {hasController ? (
            <TiptapCollabPanel
              controller={commentsController}
              getThreadLinkUrl={getThreadLinkUrl}
              onThreadAnchorUnavailable={handleThreadAnchorUnavailable}
              suggestionsController={suggestionsController}
            />
          ) : (
            <Box className="flex flex-1 flex-col items-center justify-center px-6 py-8 text-center text-gray-500">
              <ForumOutlined
                sx={{ fontSize: 48, color: "gray.300", marginBottom: 2 }}
              />
              <Typography variant="body2">No activity yet</Typography>
            </Box>
          )}
        </Box>
      </Panel>
    </>
  );
};
